import dynamic from 'next/dynamic';
import { ComponentType } from 'react';
import { GetEventsPageDataQuery, GetFrontPageDataQuery, GetPressReleasePageDataQuery } from "@graphqlTypes";
import { ScrollProvider } from "@/providers/ScrollContext";

interface FlexibleContentMapProps {
  content: GetFrontPageDataQuery["rhPage"]["flexiblePageContent"]["content"];
  listInductees?: ListInductees;
  recentStories: PageWrapperData['recentStories'];
  initialPressReleases?: GetPressReleasePageDataQuery["initialPressReleases"];
  initialEvents?: GetEventsPageDataQuery["initialEvents"];
}

type ComponentKeys = NonNullable<FlexibleContentMapProps['content']>[number]['__typename'];

const componentMap: Record<ComponentKeys, ComponentType> = {
  FlexiblePageContentContentHeroLayout: dynamic(() => import('@/components/Hero/Hero')),
  FlexiblePageContentContentStickyNavLayout: dynamic(() => import('@/components/StickyNav/StickyNav')),
  FlexiblePageContentContentFeatureLayout: dynamic(() => import('@/components/Feature/Feature')),
  FlexiblePageContentContentCarouselStoriesLayout: dynamic(() => import('@/components/CarouselStories/CarouselStories')),
  FlexiblePageContentContentListInducteesLayout: dynamic(() => import('@/components/ListInductees/ListInductees')),
  FlexiblePageContentContentInlineRedirectLayout: dynamic(() => import('@/components/InlineRedirect/InlineRedirect')),
  FlexiblePageContentContentTitleLayout: dynamic(() => import('@/components/Title/Title')),
  FlexiblePageContentContentCarouselShopLayout: dynamic(() => import('@/components/CarouselShop/CarouselShop')),
  FlexiblePageContentContentEventsLayout: dynamic(() => import('@/components/ListingEvents/ListingEvents')),
  FlexiblePageContentContentGridInducteesLayout: dynamic(() => import('@/components/GridInductees/GridInductees')),
  FlexiblePageContentContentBannerLayout: dynamic(() => import('@/components/Banner/Banner')),
  FlexiblePageContentContentSectionEndLayout: dynamic(() => import('@/components/SectionEnd/SectionEnd')),
  FlexiblePageContentContentFloatSpotlightLayout: dynamic(() => import('@/components/FloatSpotlight/FloatSpotlight')),
  FlexiblePageContentContentQuickLinksLayout: dynamic(() => import('@/components/QuickLinks/QuickLinks')),
  FlexiblePageContentContentMilestonesLayout: dynamic(() => import('@/components/Milestones/Milestones')),
  FlexiblePageContentContentTextImageLayout: dynamic(() => import('@/components/TextImage/TextImage')),
  FlexiblePageContentContentQuoteCarouselLayout: dynamic(() => import('@/components/QuoteCarousel/QuoteCarousel')),
  FlexiblePageContentContentLogosLayout: dynamic(() => import('@/components/Logos/Logos')),
  FlexiblePageContentContentFormEmbedLayout: dynamic(() => import('@/components/FormEmbed/FormEmbed')),
  FlexiblePageContentContentComparisonTableLayout: dynamic(() => import('@/components/ComparisonTable/ComparisonTable')),
  FlexiblePageContentContentAccordionLayout: dynamic(() => import('@/components/Accordion/Accordion')),
  // FlexiblePageContentContentHoursLayout: dynamic(() => import('@/components/Hours/Hours')),
  FlexiblePageContentContentHoursManualLayout: dynamic(() => import('@/components/HoursManual/HoursManual')),
  FlexiblePageContentContentListLayout: dynamic(() => import('@/components/List/List')),
  FlexiblePageContentContentPricingLayout: dynamic(() => import('@/components/Pricing/Pricing')),
  FlexiblePageContentContentIconGridLayout: dynamic(() => import('@/components/IconGrid/IconGrid')),
  FlexiblePageContentContentImageSingularLayout: dynamic(() => import('@/components/ImageSingular/ImageSingular')),
  FlexiblePageContentContentImageGalleryLayout: dynamic(() => import('@/components/ImageGallery/ImageGallery')),
  FlexiblePageContentContentVideoPlaylistLayout: dynamic(() => import('@/components/VideoPlaylist/VideoPlaylist')),
  FlexiblePageContentContentVideoSingularLayout: dynamic(() => import('@/components/VideoSingular/VideoSingular')),
  FlexiblePageContentContentVideoCarouselLayout: dynamic(() => import('@/components/VideoCarousel/VideoCarousel')),
  FlexiblePageContentContentCarouselInducteesLayout: dynamic(() => import('@/components/CarouselInductees/CarouselInductees')),
  FlexiblePageContentContentGoogleMapLayout: dynamic(() => import('@/components/GoogleMap/GoogleMap')),
  FlexiblePageContentContentWifiFormLayout: dynamic(() => import('@/components/WifiForm/WifiForm')),
  FlexiblePageContentContentFeatureEventsLayout: dynamic(() => import('@/components/FeatureEvents/FeatureEvents')),
  FlexiblePageContentContentListingEventsFullLayout: dynamic(() => import('@/components/ListingEventsFull/ListingEventsFull')),
  FlexiblePageContentContentIntroLayout: dynamic(() => import('@/components/Intro/Intro')),
  FlexiblePageContentContentTicketingLayout: dynamic(() => import('@/components/Ticketing/Ticketing')),
  FlexiblePageContentContentDonateFormLayout: dynamic(() => import('@/components/DonateForm/DonateForm')),
  FlexiblePageContentContentDetailHeroLayout: dynamic(() => import('@/components/DetailHero/FlexibleContentDetailHero')),
  FlexiblePageContentContentRichTextLayout: dynamic(() => import('@/components/RichText/RichText')),
  FlexiblePageContentContentGenericEmbedLayout: dynamic(() => import('@/components/GenericEmbed/GenericEmbed')),
  FlexiblePageContentContentCarouselRhtvLayout: dynamic(() => import('@/components/CarouselRockHallTv/CarouselRockHallTv')),
  FlexiblePageContentContentFeatureExhibitsInteractivesLayout: dynamic(() => import('@/components/FeatureExhibitsInteractives/FeatureExhibitsInteractives')),
  FlexiblePageContentContentListingPressReleasesLayout: dynamic(() => import('@/components/ListingPressReleases/ListingPressReleases')),
  FlexiblePageContentContentArtifactSingularLayout: dynamic(() => import('@/components/ArtifactSingular/ArtifactSingular')),
  FlexiblePageContentContentQuizLayout: dynamic(() => import('@/components/DigitalExhibit/DigitalExhibitQuiz/DigitalExhibitQuiz')),
};

const componentUsesScrollProvider = [
  'FlexiblePageContentContentStickyNavLayout',
];

export default function FlexibleContentMap({ content, listInductees, recentStories, initialPressReleases, initialEvents }: FlexibleContentMapProps) {
  return (
    <>
      {content?.map((component, index) => {
        const Component = componentMap[component.__typename];
        if (!Component) {
          console.warn(`No component found for type: ${component.__typename}`);
          return null;
        }
        const componentProps: Record<string, any> = { ...component };
        if (component.__typename === 'FlexiblePageContentContentListInducteesLayout') {
          if (!listInductees?.length && !componentProps.nomineeVariant) return null;
          componentProps.listInductees = listInductees;
        }
        if (component.__typename === "FlexiblePageContentContentCarouselStoriesLayout") {
          componentProps.recentStories = recentStories;
        }
        if (component.__typename === "FlexiblePageContentContentListingPressReleasesLayout") {
          componentProps.initialPressReleases = initialPressReleases;
        }
        if (component.__typename === "FlexiblePageContentContentListingEventsFullLayout") {
          componentProps.initialEvents = initialEvents;
        }
        if (component.__typename === "FlexiblePageContentContentQuizLayout") {
          componentProps.isDefault = true;
        }
        if (componentUsesScrollProvider.indexOf(component.__typename) > -1) {
          
          return (
            <ScrollProvider key={index}>
              <Component {...componentProps}  />
            </ScrollProvider>
          );
        }
        return <Component {...componentProps} key={index} />;
      })}
    </>
  );
}